import React, { useCallback, useMemo, useSyncExternalStore } from 'react';
import PlayerTimelineButton from './PlayerTimelineButton';
import { getPlayerTimeFromSegmentTime } from '../timeUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtils/constants';
import { updateClipData } from '../clipUpdateDataUtils';
import { getDeletesForSegment } from '../playerConfigUtil';
import { getAllSegments } from '../clipUpdateDataUtilsWithSplits/compositeUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { defaultTimelineSelectionState, transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { approxGTE, approxLTE } from '@/libs/compare';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';

export default function DeleteSelected() {
  const { startTime, endTime, isSelecting, startSegmentId, endSegmentId } = useSyncExternalStore(
    transcriptTimelineSelectionStore.subscribe,
    transcriptTimelineSelectionStore.getSnapshot
  );

  const { seekTo } = useRemotionPlayerSeek();

  const { clipData, clipId } = useClipsContext();

  const shouldShowDeleteButton = useMemo(() => {
    if (!isSelecting && startTime !== null && endTime !== null && startSegmentId !== null && endSegmentId !== null) {
      const allSegments = getAllSegments(clipData);
      const startSegment = allSegments.find(segment => segment.id === startSegmentId);
      if (!startSegment) {
        return false;
      }
      const endSegment = allSegments.find(segment => segment.id === endSegmentId);
      if (!endSegment) {
        return false;
      }

      if (startSegment.type === SegmentType.VIDEO || endSegment.type === SegmentType.VIDEO) {
        return false;
      }

      if (startSegmentId !== endSegmentId) {
        return true;
      }

      const deletes = getDeletesForSegment(clipData, startSegmentId);

      if (!deletes) {
        return true;
      }

      const isTotallyEnclosedByDelete = Object.values(deletes).some(deleteItem => {
        const deleteStart = deleteItem.bounds[0];
        const deleteEnd = deleteItem.bounds[1];

        // Check if the selected time range is completely within a delete range
        return approxGTE(startTime, deleteStart) && approxLTE(endTime, deleteEnd);
      });

      return !isTotallyEnclosedByDelete;
    }
    return false;
    // Start and EndTime are not added here to prevent unwanted calculations since we are not showing the button while dragging
  }, [isSelecting, startTime, endTime, startSegmentId, endSegmentId, clipData]);

  const clickHandler = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      if (startTime === null || endTime === null) {
        return;
      }

      const newClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
        startTime: startTime,
        startSegmentId: startSegmentId || '',
        endSegmentId: endSegmentId || '',
        endTime: endTime,
        deleteType: 'MANUAL'
      });

      if (startSegmentId) {
        seekTo(getPlayerTimeFromSegmentTime(startTime, newClipData, startSegmentId));
      }
      transcriptTimelineSelectionStore.set(() => ({
        ...defaultTimelineSelectionState
      }));
    },
    [clipId, endSegmentId, endTime, seekTo, startSegmentId, startTime]
  );

  if (!shouldShowDeleteButton) {
    return null;
  }

  return (
    <PlayerTimelineButton
      iconName="IconTrash"
      onClick={clickHandler}
      onMouseDown={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
      tooltipTitle="Delete"
      buttonId="delete-selected"
    />
  );
}
